/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {useLayoutEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import Box from '@material-ui/core/Box';
import Divider from '@material-ui/core/Divider';
import Link from '@material-ui/core/Link';
import {makeStyles} from '@material-ui/core/styles';
import classnames from 'classnames';
import useHasMounted from '@ion/core/src/hooks/useHasMounted';
import {translate} from '@ion/core/src/locale/locale';
import {CVSAssetType} from '@ion/core/src/types/entities';
import Breadcrumb from '@ion/dls/src/components/breadcrumbs/Breadcrumbs';
import {TertiaryButton} from '@ion/dls/src/components/buttons/TertiaryButton';
import Tooltip from '@ion/dls/src/components/tooltip/Tooltip';
import {NavigationIcon} from '@ion/dls/src/icons/NavigationIcon';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {AssetDialog} from '@/components/dialogs/AssetDialog';
import {clearSelectedAsset, showAssetDialog} from '@/store/actions/dashboard';
import {
  assetDialogSelector,
  assetsSelector
} from '@/store/selectors/dashboard-selectors';
import {useAssetDetails} from '../../hooks/useAssetDetails';
import {checkOverflow} from '../../utils/ComponentUtils';
import Images from '../img/ImagePaths';
import AssetDetails from './AssetDetails';
import SubscriptionDetails from './SubscriptionDetails';

interface AssetSideSheetDetailProps {
  asset: CVSAssetType;
}

export const getImageClass = ({color}: {color: string}) => ({
  borderRadius    : '50%',
  height          : '64px',
  width           : '64px',
  border          : `3px solid ${color}`,
  backgroundColor : color
});

const useStyles = makeStyles(theme => ({
  editClass : {
    color : dlsColor('caBlue')
  },
  imgClass       : getImageClass,
  clickableCrumb : {
    '&:hover' : {
      cursor : 'pointer'
    }
  },
  assetDataValue : {
    fontWeight : 500,
    fontStyle  : 'normal',
    fontSize   : '12px',
    lineHeight : '16px',
    padding    : '4px',
    color      : dlsColor('grey3')
  },
  header : {
    position        : 'fixed',
    width           : 451,
    zIndex          : 1,
    backgroundColor : dlsColor('grey6')
  },
  assetName : {
    width        : '260px',
    textOverflow : 'ellipsis',
    overflow     : 'hidden',
    whiteSpace   : 'nowrap'
  },
  titleClass : {
    padding    : 0,
    fontWeight : 500,
    fontSize   : '24px',
    color      : dlsColor('grey2')
  }
}));

/**
 * Defines the AssetSideSheetDetail Component.
 *
 * ```typescript
 *
 * <AssetSideSheetDetail asset={asset} />
 *
 * ```
 */
const AssetSideSheetDetail = ({
  asset
}: AssetSideSheetDetailProps) => {
  const dispatch         = useDispatch();
  const crumbAssetRef    = useRef(null);
  const titleAssetRef    = useRef(null);
  const isMounted        = useHasMounted();
  const assets           = useSelector(assetsSelector);
  const assetDialogShow  = useSelector(assetDialogSelector);
  const {color}          = useAssetDetails(asset);
  const classes          = useStyles({color});

  const [overflowed, setIsOverflowed] = useState({crumbAssetName: false, titleAssetName: false});

  const otherAssetsExist = assets.length > 1;
  const {
    name,
    year,
    make,
    model
  } = asset;
  const panelBreadcrumbs = [
    {
      label   : translate('sidesheet:assets'),
      click   : true,
      onClick : switchToAssetSideSheetList
    },
    {
      label : `${name}`,
      click : false
    }
  ];
  const assetLabels        = {...translate('sidesheet:assetDetails', {returnObjects: true})};
  const subscriptionLabels = {...translate('sidesheet:subscriptionDetails', {returnObjects: true})};

  useLayoutEffect(() => {
    if (isMounted) {
      setIsOverflowed(previous => ({
        ...previous,
        crumbAssetName : checkOverflow(crumbAssetRef.current),
        titleAssetName : checkOverflow(titleAssetRef.current)
      }));
    }
  }, [isMounted, asset]);

  function switchToAssetSideSheetList() {
    dispatch(clearSelectedAsset());
  };

  function launchAssetDialog() {
    dispatch(showAssetDialog(true));
  };

  return (
    <>
      <div className={classes.header}>
        {
          otherAssetsExist && (
            <>
              <Breadcrumb icon={<NavigationIcon direction='back' />}
                iconClick={switchToAssetSideSheetList} >
                {panelBreadcrumbs.map(crumb => {
                  return (
                    <Link key={`crumb-${crumb.label}`} style={{textDecoration: 'none'}}
                      className={crumb.click ? classes.clickableCrumb : undefined}
                      onClick={crumb.click ? crumb.onClick : undefined}>
                      {crumb.click
                        ? `${crumb.label}`
                        : <Tooltip title={crumb.label}
                          disableHoverListener={overflowed.crumbAssetName}>
                          <div ref={crumbAssetRef} className={classes.assetName}>{`${crumb.label}`}</div>
                        </Tooltip>
                      }
                    </Link>
                  );
                })}
              </Breadcrumb>
              <Divider />
            </>
          )
        }
        <div style={{padding: '10px'}}>
          <Box display='flex' alignItems='center' justifyContent= 'space-between'>
            <Box>
              <Box display='flex' alignItems='center'>
                <img src={Images.BMWAvatar} className={classes.imgClass}/>
                <Box paddingLeft='8px'>
                  <Tooltip title={name}
                    disableHoverListener={overflowed.titleAssetName}>
                    <div ref={titleAssetRef}
                      className={classnames(classes.titleClass, classes.assetName)}>
                      <span>{name}</span>
                    </div>
                  </Tooltip>
                  {(year || make || model) &&
                    <div className={classes.assetDataValue}>
                      {year} {make} {model}
                    </div>}
                </Box>
              </Box>
            </Box>
            <Box><TertiaryButton className={classes.editClass} onClick={launchAssetDialog} >{translate('common:edit')}</TertiaryButton></Box>
          </Box>
        </div>
      </div>
      <>
        <div style={{paddingTop: otherAssetsExist ? '160px' : '90px'}}>
          <AssetDetails asset={asset} labels={assetLabels}/>
        </div>
        <div style={{paddingTop: '10px'}}>
          <SubscriptionDetails asset={asset} labels={subscriptionLabels}/>
        </div>
      </>
      {assetDialogShow && <AssetDialog />}
    </>
  );
};

export default AssetSideSheetDetail;
